// ============================================
// INFRASTRUCTURE - Reportes Repository
// ============================================

import { axiosClient } from './axios-client';

export interface RecaudacionChartItem {
  dia: string;
  total: number;
}

export interface TopProductoItem {
  nombreProducto: string;
  cantidadVendida: number;
  totalVendido: number;
}

export interface TopProductoTotalVentaItem {
  nombreProducto: string;
  cantidadVendida: number;
  totalVendido: number;
}

export interface UtilidadCostoData {
  ventaTotal: number;
  ventaCalculadora: number;
  ventaInventario: number;
  costo: number;
  utilidad: number;
}

export interface TotalesGenerales {
  totalEfectivo: number;
  totalDebito: number;
  totalCredito: number;
  totalFiado: number;
  totalTransferencia: number;
  totalVenta: number;
  utilidadTotal: number;
}

export interface ReporteDiarioItem {
  fechaVenta: string;
  nombreProducto: string;
  tipoOrigen: string;
  cantidadTotal: number;
  totalEfectivo: number;
  totalDebito: number;
  totalCredito: number;
  totalFiado: number;
  totalTransferencia: number;
  totalVentaBruta: number;
  utilidadReal: number;
}

export interface ReporteDiarioResponse {
  items: ReporteDiarioItem[];
  totalPaginas: number;
  paginaActual: number;
}

export interface AlertaInventarioItem {
  nombreProducto: string;
  codigo?: string;
  productoAlmacenId?: number;
  fechaVencimiento: string;
  stock: number;
  estado: 'VENCIDO' | 'POR_VENCER';
}

export interface DetalleVentaProductoItem {
  nombreProducto: string;
  categoria: string;
  cantidadVendida: number;
  precioCompra: number;
  precioVenta: number;
  utilidad: number;
  utilidadTotal: number;
}

export interface DetalleVentaProductoResponse {
  items: DetalleVentaProductoItem[];
  totalPaginas: number;
  totalElementos: number;
  paginaActual: number;
}

export interface DetalleVentasCategoriaAlmacenItem {
  categoriaId: number;
  categoriaNombre: string;
  tipoCategoria: 'PROPIA' | 'GLOBAL' | 'NINGUNA';
  cantidadVendida: number;
  totalVenta: number;
  utilidadTotal: number;
}

// Respuesta de POST /reportes/busquedas. `limiteDiario` y `restantes` vienen null en premium
// (sin límite). `reutilizada` indica que se devolvió una búsqueda reciente igual, sin descontar cupo.
export interface BusquedaReporteResponse {
  busquedaId: number;
  premium: boolean;
  limiteDiario: number | null;
  restantes: number | null;
  reutilizada: boolean;
}

// Config opcional aceptada por cada método: `signal`, para poder cancelar la petición
// (AbortSignal de TanStack Query o de un AbortController propio), y `busquedaId`, el
// comprobante que entrega iniciarBusqueda: el backend lo exige solo al plan gratuito y lo
// ignora en premium. Es aditivo: quien no lo pase mantiene el comportamiento actual.
export interface ReportesRequestOptions {
  signal?: AbortSignal;
  busquedaId?: number;
}

// El reporte diario pagina por días. `tamano` es opcional (el servidor usa 10 por defecto y
// acepta hasta 400, lo que cubre los 12 meses máximos): la exportación lo usa para traer todo
// el rango en una sola llamada en vez de una por cada 10 días.
export interface ReportesDiarioOptions extends ReportesRequestOptions {
  tamano?: number;
}

export const reportesRepository = {
  /**
   * Registrar el clic en Buscar: valida el rango según el plan, descuenta del cupo diario del
   * plan gratuito y devuelve el comprobante (busquedaId) para las consultas siguientes.
   * POST /reportes/busquedas
   */
  async iniciarBusqueda(desde: string, hasta: string): Promise<BusquedaReporteResponse> {
    const { data } = await axiosClient.post(`/reportes/busquedas`, { desde, hasta });
    return data;
  },

  /**
   * Obtener datos de recaudación para gráfico
   */
  async getRecaudacionChart(desde: string, hasta: string, opts?: ReportesRequestOptions): Promise<RecaudacionChartItem[]> {
    const { data } = await axiosClient.get(`/reportes/recaudacion-chart`, {
      params: { desde, hasta, busquedaId: opts?.busquedaId },
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener top 10 productos más vendidos
   */
  async getTopProductos(desde: string, hasta: string, opts?: ReportesRequestOptions): Promise<TopProductoItem[]> {
    const { data } = await axiosClient.get(`/reportes/top-productos`, {
      params: { desde, hasta, busquedaId: opts?.busquedaId },
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener top 10 productos con más totalde ventas
   */
  async getTopProductosTotalVenta(desde: string, hasta: string, opts?: ReportesRequestOptions): Promise<TopProductoTotalVentaItem[]> {
    const { data } = await axiosClient.get(`/reportes/top-productos-venta`, {
      params: { desde, hasta, busquedaId: opts?.busquedaId },
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener utilidad y costo
   */
  async getUtilidadCosto(desde: string, hasta: string, opts?: ReportesRequestOptions): Promise<UtilidadCostoData> {
    const { data } = await axiosClient.get(`/reportes/utilidad-costo`, {
      params: { desde, hasta, busquedaId: opts?.busquedaId },
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener reporte diario detallado
   */
  async getReporteDiario(
    desde: string,
    hasta: string,
    pagina: number,
    opts?: ReportesDiarioOptions
  ): Promise<ReporteDiarioResponse> {
    const { data } = await axiosClient.get(`/reportes/diario`, {
      params: {
        desde,
        hasta,
        pagina,
        ...(opts?.tamano ? { tamano: opts.tamano } : {}),
        busquedaId: opts?.busquedaId,
      },
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener totales generales
   */
  async getTotalesGenerales(desde: string, hasta: string, opts?: ReportesRequestOptions): Promise<TotalesGenerales> {
    const { data } = await axiosClient.get(`/reportes/totales`, {
      params: { desde, hasta, busquedaId: opts?.busquedaId },
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener alertas de inventario
   */
  async getAlertasInventario(opts?: ReportesRequestOptions): Promise<AlertaInventarioItem[]> {
    const { data } = await axiosClient.get(`/reportes/alertas-inventario`, {
      signal: opts?.signal,
    });
    return data;
  },

  /**
   * Obtener detalle de ventas por producto
   */
  async getDetalleVentasProducto(
    desde: string,
    hasta: string,
    pagina: number,
    tamano: number = 10,
    opts?: ReportesRequestOptions
  ): Promise<DetalleVentaProductoResponse> {
    const { data } = await axiosClient.get(`/reportes/detalle-ventas-producto`, {
      params: { desde, hasta, pagina, tamano, busquedaId: opts?.busquedaId },
      signal: opts?.signal,
    });
    return data;
  },

  // Solo premium: el backend no pide comprobante de búsqueda en este endpoint.
  async getDetalleVentasCategoriaAlmacen(
    desde: string,
    hasta: string,
    opts?: ReportesRequestOptions
  ): Promise<DetalleVentasCategoriaAlmacenItem[]> {
    const { data } = await axiosClient.get(`/reportes/detalle-ventas-categoria-almacen`, {
      params: { desde, hasta },
      signal: opts?.signal,
    });
    return data;
  },
};
